import { useState, type FormEvent } from "react";
import { signUp, type SignUpOutput } from "aws-amplify/auth";
import { ErrorMessage } from "./error-message.js";
import { friendlyAuthError } from "./cognito-errors.js";

interface Props {
  /** When set (invitation flow), email is pre-filled and read-only. */
  fixedEmail?: string;
  onResult: (
    result: SignUpOutput,
    credentials: { username: string; password: string },
  ) => void;
}

/**
 * Email + password sign-up form. On submit calls Amplify v6 `signUp`
 * which sends a 6-digit confirmation code to the user's email; the
 * caller renders <EmailConfirmationForm> next based on `nextStep`.
 *
 * Two modes:
 *   - **self-signup**: caller passes no `fixedEmail`. User picks email.
 *   - **invitation accept**: caller passes `fixedEmail` from the
 *     previewInvitation response. Email field is rendered read-only.
 *     The post-confirmation Lambda finds the pending invitation by
 *     email and accepts it (Path A).
 *
 * Password policy mirror (purely client-side hint; Cognito enforces):
 *   min 14 chars, lowercase + uppercase + digit + symbol.
 */
export function SignUpForm({ fixedEmail, onResult }: Props) {
  const [email, setEmail] = useState(fixedEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordIssue = checkPasswordPolicy(password);
  const passwordsMatch =
    password.length === 0 || password === confirmPassword;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    if (passwordIssue) {
      setError(passwordIssue);
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords don't match.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      onResult(result, { username: email, password });
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <div>
        <label
          htmlFor="signup-email"
          className="mb-1 block text-sm font-medium"
        >
          Email
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          required
          readOnly={Boolean(fixedEmail)}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-base shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:disabled:bg-zinc-800"
          disabled={submitting}
        />
      </div>
      <div>
        <label
          htmlFor="signup-password"
          className="mb-1 block text-sm font-medium"
        >
          Password
        </label>
        <input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-base shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
          aria-describedby="signup-password-hint"
        />
        <p
          id="signup-password-hint"
          className="mt-1 text-xs text-zinc-500 dark:text-zinc-400"
        >
          14+ characters with lowercase, uppercase, digit, and symbol.
        </p>
      </div>
      <div>
        <label
          htmlFor="signup-confirm-password"
          className="mb-1 block text-sm font-medium"
        >
          Confirm password
        </label>
        <input
          id="signup-confirm-password"
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-base shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
          aria-invalid={!passwordsMatch}
        />
        {!passwordsMatch && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            Passwords don't match.
          </p>
        )}
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <button
        type="submit"
        disabled={submitting || Boolean(passwordIssue) || !passwordsMatch}
        className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-base font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Creating account…" : "Create account"}
      </button>
    </form>
  );
}

function checkPasswordPolicy(p: string): string | null {
  if (p.length === 0) return null;
  if (p.length < 14) return "Password must be at least 14 characters.";
  if (!/[a-z]/.test(p)) return "Password must include a lowercase letter.";
  if (!/[A-Z]/.test(p)) return "Password must include an uppercase letter.";
  if (!/\d/.test(p)) return "Password must include a digit.";
  if (!/[^A-Za-z0-9]/.test(p)) return "Password must include a symbol.";
  return null;
}
